import { Pagination } from "antd";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FaTrash } from "react-icons/fa";
import { FiPlus } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { Button, Card, Col, Input, Row, Table } from "reactstrap";
import Header from "../../components/globals/Header";
import SkeletonLoader from "../../components/loaders/SkeletonLoader";
import AddReviewModal from "../../components/modals/AddReviewModal";
import DeleteModal from "../../components/modals/DeleteModal";
import PageName from "../../components/react_helmet/PageName";
import { deleteDocument } from "../../firebase/utility";

const Reviews = () => {
    const location = useLocation();
    const filterProductId = location?.state?.productId;
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [addReviewModal, setAddReviewModal] = useState(false);
    const [selectedReview, setSelectedReview] = useState(null);
    const [filterRating, setFilterRating] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const toggleDeleteModal = () => setDeleteModal(!deleteModal);
    const toggleAddReviewModal = () => setAddReviewModal(!addReviewModal);

    const fetchReviews = async () => {
        setLoading(true);
        try {
            const snapshot = await firebase.firestore().collection("Reviews").get();
            let data = snapshot.docs.map(doc => ({
                ...doc.data(),
                _id: doc.id
            }));

            if (filterProductId) {
                data = data.filter(r => r.productId === filterProductId);
            }

            // Fetch product names for all unique productIds
            const uniqueProductIds = [...new Set(data.map(r => r.productId).filter(Boolean))];
            const productNamesMap = {};

            await Promise.all(
                uniqueProductIds.map(async (id) => {
                    try {
                        const productSnap = await firebase.firestore().collection("Products").doc(id).get();
                        if (productSnap.exists) {
                            productNamesMap[id] = productSnap.data().name;
                        }
                    } catch (err) {
                        console.error("Error fetching product title", err);
                    }
                })
            );

            // Attach productName to each review
            data = data?.map(r => ({
                ...r,
                productName: productNamesMap[r.productId] || r.productId
            }));

            // Sort by date descending (newest first)
            const sortedData = data.sort((a, b) => {
                const dateA = a.createdAt?.seconds ? a.createdAt.seconds * 1000 : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
                const dateB = b.createdAt?.seconds ? b.createdAt.seconds * 1000 : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
                return dateB - dateA;
            });
            setReviews(sortedData);
            setCurrentPage(1); // Reset to first page on fetch
        } catch (error) {
            console.error("Error fetching reviews:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedReview?._id) return;
        try {
            await deleteDocument("Reviews", selectedReview._id, "_id");
            toast.success("Review deleted successfully");
            fetchReviews();
        } catch (error) {
            console.error("Error deleting review:", error);
            toast.error("Failed to delete review");
        }
    };

    useEffect(() => {
        fetchReviews();
    }, []);

    const filteredReviews = useMemo(() => {
        let result = reviews;
        if (filterRating !== "all") {
            result = reviews.filter(r => Number(r.rating) === Number(filterRating));
        }
        return result;
    }, [reviews, filterRating]);

    // Calculate paginated reviews
    const currentReviews = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return filteredReviews.slice(startIndex, startIndex + pageSize);
    }, [filteredReviews, currentPage]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    return (
        <>
            <PageName title={"Product Reviews"} />

            <div className="mx-5">
                <Row className="mb-4 align-items-center">
                    <Col md={6}>
                        <Header title={"Product Reviews"} />
                    </Col>
                    <Col md={6} className="d-flex justify-content-end">
                        <div className="d-flex align-items-center gap-3">
                            <div className="d-flex align-items-center gap-2">
                                <span className="fw-semibold text-muted text-nowrap" style={{ fontSize: '14px' }}>Filter:</span>
                                <Input
                                    type="select"
                                    value={filterRating}
                                    onChange={(e) => {
                                        setFilterRating(e.target.value);
                                        setCurrentPage(1); // Reset page on filter
                                    }}
                                    style={{ minWidth: '130px', cursor: 'pointer' }}
                                >
                                    <option value="all">All Ratings</option>
                                    <option value="5">5 Stars</option>
                                    <option value="4">4 Stars</option>
                                    <option value="3">3 Stars</option>
                                    <option value="2">2 Stars</option>
                                    <option value="1">1 Star</option>
                                </Input>
                            </div>
                            <Button
                                color="primary"
                                className="d-flex align-items-center gap-2 fw-semibold px-3 py-2 shadow-sm rounded-2"
                                onClick={toggleAddReviewModal}
                            >
                                <FiPlus size={16} /> Add Review
                            </Button>
                        </div>
                    </Col>
                </Row>
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <Table responsive
                        hover
                        className="mb-0 align-middle"
                        style={{ fontSize: "14px", borderCollapse: 'separate', borderSpacing: '0' }}>
                        <thead className="bg-light text-secondary border-bottom">
                            <tr>
                                <th className="py-3 px-4 fw-bold text-uppercase border-0" style={{ width: '20%', fontSize: '12px', letterSpacing: '0.5px' }}>Product</th>
                                <th className="py-3 px-4 fw-bold text-uppercase border-0" style={{ width: '15%', fontSize: '12px', letterSpacing: '0.5px' }}>Customer</th>
                                <th className="py-3 px-4 fw-bold text-uppercase border-0" style={{ width: '15%', fontSize: '12px', letterSpacing: '0.5px' }}>Rating</th>
                                <th className="py-3 px-4 fw-bold text-uppercase border-0" style={{ width: '35%', fontSize: '12px', letterSpacing: '0.5px' }}>Comment</th>
                                <th className="py-3 px-4 fw-bold text-uppercase border-0" style={{ width: '10%', fontSize: '12px', letterSpacing: '0.5px' }}>Date</th>
                                <th className="py-3 px-4 fw-bold text-uppercase border-0 text-center" style={{ width: '5%', fontSize: '12px', letterSpacing: '0.5px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                Array.from({ length: 5 }).map((_, idx) => (
                                    <tr key={idx}>
                                        <td className="px-4 py-3"><SkeletonLoader height="16px" width="70%" /></td>
                                        <td className="px-4 py-3"><SkeletonLoader height="16px" width="80%" /><SkeletonLoader height="12px" width="50%" className="mt-2" /></td>
                                        <td className="px-4 py-3"><SkeletonLoader height="16px" width="60%" /></td>
                                        <td className="px-4 py-3"><SkeletonLoader height="16px" width="100%" /></td>
                                        <td className="px-4 py-3"><SkeletonLoader height="16px" width="80%" /></td>
                                        <td className="px-4 py-3"><SkeletonLoader height="24px" width="24px" borderRadius="12px" className="mx-auto" /></td>
                                    </tr>
                                ))
                            ) : currentReviews.length > 0 ? (
                                currentReviews.map((review, index) => (
                                    <tr key={index} className="border-bottom">
                                        <td className="px-4 py-3 fw-bold text-dark">{review.productName || review.productId}</td>
                                        <td className="px-4 py-3">
                                            <div className="d-flex flex-column">
                                                <span className="fw-semibold text-dark">{review.name || "Anonymous"}</span>
                                                <small className="text-muted" style={{ fontSize: '11px' }}>{review.userId}</small>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="d-flex flex-column justify-content-center">
                                                <div className="text-warning mb-1" style={{ fontSize: '16px', letterSpacing: '1px' }}>
                                                    {"★".repeat(review.rating)}{"☆".repeat(5 - review.rating)}
                                                </div>
                                                <span className="fw-semibold text-muted" style={{ fontSize: '12px' }}>{review.rating} out of 5</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-secondary" style={{ whiteSpace: 'normal', maxWidth: '350px', lineHeight: '1.5' }}>
                                            {review.comment ? (
                                                <span>"{review.comment}"</span>
                                            ) : (
                                                <span className="fst-italic text-muted">No comment provided.</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-muted fw-medium" style={{ fontSize: '13px' }}>
                                            {moment(review.date).format("L") || (review.createdAt?.toDate?.() || new Date(review.createdAt)).toLocaleDateString()}
                                        </td>
                                        <td className="px-4 py-3 text-center">
                                            <Button
                                                color="light"
                                                className="text-danger p-2 rounded-circle hover-scale"
                                                onClick={() => {
                                                    setSelectedReview(review);
                                                    toggleDeleteModal();
                                                }}
                                                title="Delete Review"
                                            >
                                                <FaTrash size={14} />
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-5 text-muted fw-medium bg-light">
                                        No reviews found matching the criteria.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                    {!loading && filteredReviews.length > pageSize && (
                        <div className="d-flex justify-content-center p-4 border-top bg-white">
                            <Pagination
                                current={currentPage}
                                total={filteredReviews.length}
                                pageSize={pageSize}
                                onChange={handlePageChange}
                                showSizeChanger={false}
                            />
                        </div>
                    )}
                </Card>
            </div>

            <DeleteModal
                modal={deleteModal}
                toggle={toggleDeleteModal}
                handleDelete={handleDelete}
            />

            <AddReviewModal
                modal={addReviewModal}
                toggle={toggleAddReviewModal}
                fetchReviews={fetchReviews}
            />
        </>
    );
};

export default Reviews;
